import React, { useEffect, useRef, useState } from 'react';
import PlacesAutocomplete, { geocodeByAddress } from 'react-places-autocomplete';
import { COUNTRIES } from '../../../constants/countries';

/*
 * BusinessAddressFields — country-driven business address form.
 *
 * The country <select> sits at the TOP; choosing a country swaps the field set
 * below to that country's postal format:
 *   · United States → Street (Places) · City · State (dropdown) · ZIP
 *   · Thailand      → Street/Soi · Province · District · Sub-district · Postal
 *                     (province→district→sub-district cascade off /api/geo/th/*,
 *                      sub-district auto-fills the postal code; labels bilingual)
 *   · anywhere else → Street (Places) · City · Region · Postal  (generic)
 *
 * Storage mapping (see ensureSchema.js): admin-1 → b_state, admin-2 → b_city,
 * admin-3 → b_subdistrict, postal → b_zip_code, street → b_address. The flat
 * columns hold romanized (English) display values; the full bilingual record +
 * stable codes go in b_address_components (value.addressComponents) so the
 * address can be rendered in either script and matched by code.
 *
 * Props:
 *   value:      { address, city, state, subdistrict, zip, country,
 *                 addressComponents, showAddress }
 *   onChange:   (patch) => void   — merge a partial update into the parent state
 *   inputStyle: shared input style object (matches the rest of the editor)
 */

// Minimal US state list (value = USPS abbreviation, which is what b_state has
// stored for US businesses). Full name shown to the user.
const US_STATES = [
  ['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'],
  ['CA', 'California'], ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'],
  ['FL', 'Florida'], ['GA', 'Georgia'], ['HI', 'Hawaii'], ['ID', 'Idaho'],
  ['IL', 'Illinois'], ['IN', 'Indiana'], ['IA', 'Iowa'], ['KS', 'Kansas'],
  ['KY', 'Kentucky'], ['LA', 'Louisiana'], ['ME', 'Maine'], ['MD', 'Maryland'],
  ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'], ['MS', 'Mississippi'],
  ['MO', 'Missouri'], ['MT', 'Montana'], ['NE', 'Nebraska'], ['NV', 'Nevada'],
  ['NH', 'New Hampshire'], ['NJ', 'New Jersey'], ['NM', 'New Mexico'], ['NY', 'New York'],
  ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'], ['OK', 'Oklahoma'],
  ['OR', 'Oregon'], ['PA', 'Pennsylvania'], ['RI', 'Rhode Island'], ['SC', 'South Carolina'],
  ['SD', 'South Dakota'], ['TN', 'Tennessee'], ['TX', 'Texas'], ['UT', 'Utah'],
  ['VT', 'Vermont'], ['VA', 'Virginia'], ['WA', 'Washington'], ['WV', 'West Virginia'],
  ['WI', 'Wisconsin'], ['WY', 'Wyoming'], ['DC', 'District of Columbia'],
];
const US_STATE_BY_NAME = US_STATES.reduce((m, [abbr, name]) => {
  m[name.toLowerCase()] = abbr;
  return m;
}, {});

// Bilingual option label: "Din Daeng (ดินแดง)".
const biLabel = (o) => (o.nameTh && o.nameTh !== o.nameEn ? `${o.nameEn} (${o.nameTh})` : o.nameEn);

export default function BusinessAddressFields({ value, onChange, inputStyle }) {
  const v = value || {};
  const country = v.country || '';
  const isUS = country === 'United States';
  const isTH = country === 'Thailand';
  const dim = { ...inputStyle, opacity: v.showAddress ? 1 : 0.3 };

  // ── Thai cascade state ──
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [subdistricts, setSubdistricts] = useState([]);
  const comp = v.addressComponents || {};
  const provinceCode = comp.province?.code ?? '';
  const districtCode = comp.district?.code ?? '';
  const subdistrictCode = comp.subdistrict?.code ?? '';
  // Guard so the initial hydration fetch (districts/subdistricts for an already-
  // saved TH address) runs once, not on every keystroke.
  const hydratedFor = useRef(null);

  // Load provinces once when the Thai form is active.
  useEffect(() => {
    if (!isTH || provinces.length) return;
    fetch('/api/geo/th/provinces')
      .then((r) => r.json())
      .then((j) => setProvinces(j.provinces || []))
      .catch(() => setProvinces([]));
  }, [isTH, provinces.length]);

  // Hydrate the dependent dropdowns for a saved TH address (codes come from
  // b_address_components). Runs once per loaded province/district pair.
  useEffect(() => {
    if (!isTH) return;
    const key = `${provinceCode}|${districtCode}`;
    if (hydratedFor.current === key) return;
    hydratedFor.current = key;
    if (provinceCode) {
      fetch(`/api/geo/th/districts?province=${provinceCode}`)
        .then((r) => r.json()).then((j) => setDistricts(j.districts || [])).catch(() => {});
    }
    if (districtCode) {
      fetch(`/api/geo/th/subdistricts?district=${districtCode}`)
        .then((r) => r.json()).then((j) => setSubdistricts(j.subdistricts || [])).catch(() => {});
    }
  }, [isTH, provinceCode, districtCode]);

  const handleCountryChange = (e) => {
    const next = e.target.value;
    // Switching countries invalidates the structured fields (a US state isn't a
    // Thai province). Keep the free-text street; reset everything else + the
    // cascade so stale values don't carry over.
    setDistricts([]); setSubdistricts([]); hydratedFor.current = null;
    onChange({ country: next, city: '', state: '', subdistrict: '', zip: '', addressComponents: null });
  };

  // ── Google Places parse (US + generic). Thailand uses the cascade instead. ──
  const handlePlacesSelect = async (selected) => {
    try {
      const results = await geocodeByAddress(selected);
      const parts = results[0]?.address_components || [];
      const get = (type) => parts.find((c) => c.types.includes(type))?.long_name || '';
      const street = `${get('street_number')} ${get('route')}`.trim();
      const city = get('locality') || get('sublocality') || get('neighborhood');
      const region = get('administrative_area_level_1');
      const zip = get('postal_code');
      const detectedCountry = get('country');
      onChange({
        address: street,
        city,
        state: isUS ? (US_STATE_BY_NAME[region.trim().toLowerCase()] || region) : region,
        zip,
        country: detectedCountry || country,
        addressComponents: null,
      });
    } catch (err) {
      console.error('❌ Error parsing address from Google:', err);
    }
  };

  // ── Thai cascade handlers ──
  const onProvince = (e) => {
    const code = Number(e.target.value);
    const p = provinces.find((x) => x.code === code);
    setSubdistricts([]); setDistricts([]);
    hydratedFor.current = `${code || ''}|`; // prevent the hydration effect from re-fetching
    if (code) {
      fetch(`/api/geo/th/districts?province=${code}`)
        .then((r) => r.json()).then((j) => setDistricts(j.districts || [])).catch(() => {});
    }
    onChange({
      state: p ? p.nameEn : '',
      city: '', subdistrict: '', zip: '',
      addressComponents: p ? { countryCode: 'TH', province: p } : null,
    });
  };

  const onDistrict = (e) => {
    const code = Number(e.target.value);
    const d = districts.find((x) => x.code === code);
    setSubdistricts([]);
    hydratedFor.current = `${provinceCode || ''}|${code || ''}`;
    if (code) {
      fetch(`/api/geo/th/subdistricts?district=${code}`)
        .then((r) => r.json()).then((j) => setSubdistricts(j.subdistricts || [])).catch(() => {});
    }
    onChange({
      city: d ? d.nameEn : '',
      subdistrict: '', zip: '',
      addressComponents: { ...comp, district: d || undefined, subdistrict: undefined },
    });
  };

  const onSubdistrict = (e) => {
    const code = Number(e.target.value);
    const s = subdistricts.find((x) => x.code === code);
    onChange({
      subdistrict: s ? s.nameEn : '',
      zip: s ? s.postalCode : v.zip, // auto-fill postal (still editable below)
      addressComponents: { ...comp, subdistrict: s ? { code: s.code, nameEn: s.nameEn, nameTh: s.nameTh } : undefined, postalCode: s ? s.postalCode : comp.postalCode },
    });
  };

  const eyeToggle = (
    <span
      onClick={() => onChange({ showAddress: !v.showAddress })}
      title={v.showAddress ? 'Hide from public' : 'Show on public profile'}
      style={{ cursor: 'pointer', fontSize: '16px', userSelect: 'none' }}
    >
      {v.showAddress ? '👁' : '👁‍🗨'}
    </span>
  );

  // Street field: Places autocomplete for US/generic, plain text for Thailand
  // (Google's TH component mapping is unreliable — the cascade is the source of
  // truth there, and the street/Soi is free-form anyway).
  const streetField = isTH ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <input
        type="text"
        placeholder="House No. / Building / Soi"
        value={v.address || ''}
        onChange={(e) => onChange({ address: e.target.value })}
        style={{ ...dim, width: 'calc(100% - 28px)' }}
      />
      {eyeToggle}
    </div>
  ) : (
    <PlacesAutocomplete
      value={v.address || ''}
      onChange={(val) => onChange({ address: val })}
      onSelect={handlePlacesSelect}
      searchOptions={isUS ? { types: ['address'], componentRestrictions: { country: 'us' } } : { types: ['address'] }}
    >
      {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
        <div style={{ position: 'relative', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <input {...getInputProps({ placeholder: 'Street Address' })} style={{ ...dim, width: 'calc(100% - 28px)' }} />
            {eyeToggle}
          </div>
          {suggestions.length > 0 && (
            <div style={{ position: 'absolute', zIndex: 1000, backgroundColor: 'white', border: '1px solid #ccc', width: '100%' }}>
              {loading && <div style={{ padding: '8px' }}>Loading...</div>}
              {suggestions.map((s, i) => (
                <div key={i} {...getSuggestionItemProps(s, { style: { padding: '8px', cursor: 'pointer', backgroundColor: s.active ? '#f0f0f0' : '#fff' } })}>
                  {s.description}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </PlacesAutocomplete>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
      {/* Country at the TOP — drives the field set below. */}
      <label style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '-2px' }}>Country</label>
      <select value={country} onChange={handleCountryChange} style={dim}>
        <option value="">Select a country…</option>
        {country && !COUNTRIES.includes(country) && <option value={country}>{country}</option>}
        {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>

      {streetField}

      {isTH ? (
        <>
          <select value={provinceCode} onChange={onProvince} style={dim}>
            <option value="">Province (จังหวัด)…</option>
            {provinces.map((p) => <option key={p.code} value={p.code}>{biLabel(p)}</option>)}
          </select>
          <select value={districtCode} onChange={onDistrict} style={dim} disabled={!provinceCode}>
            <option value="">District (เขต/อำเภอ)…</option>
            {districts.map((d) => <option key={d.code} value={d.code}>{biLabel(d)}</option>)}
          </select>
          <select value={subdistrictCode} onChange={onSubdistrict} style={dim} disabled={!districtCode}>
            <option value="">Sub-district (แขวง/ตำบล)…</option>
            {subdistricts.map((s) => <option key={s.code} value={s.code}>{biLabel(s)}</option>)}
          </select>
          <input
            type="text"
            placeholder="Postal Code"
            value={v.zip || ''}
            onChange={(e) => onChange({ zip: e.target.value })}
            style={dim}
          />
        </>
      ) : (
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="City"
            value={v.city || ''}
            onChange={(e) => onChange({ city: e.target.value })}
            style={dim}
            className="w-[60%]"
          />
          {isUS ? (
            <select value={v.state || ''} onChange={(e) => onChange({ state: e.target.value })} style={dim} className="w-[15%]">
              <option value="">State</option>
              {v.state && !US_STATES.some(([a]) => a === v.state) && <option value={v.state}>{v.state}</option>}
              {US_STATES.map(([abbr, name]) => <option key={abbr} value={abbr}>{`${abbr} — ${name}`}</option>)}
            </select>
          ) : (
            <input
              type="text"
              placeholder="Region"
              value={v.state || ''}
              onChange={(e) => onChange({ state: e.target.value })}
              style={dim}
              className="w-[15%]"
            />
          )}
          <input
            type="text"
            placeholder={isUS ? 'Zip Code' : 'Postal'}
            value={v.zip || ''}
            onChange={(e) => onChange({ zip: e.target.value })}
            style={dim}
            className="w-[25%]"
          />
        </div>
      )}
    </div>
  );
}
